import React, { memo } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import { TreeItem } from '@mui/x-tree-view';

// Form values interface
export interface AccountTypesFormValues {
  excludedAccountTypes: number[];
}

// Tree structure types
export interface AccountTypeTreeItem {
  id: string;
  name: string;
  description: string;
  code: number;
}

interface AccountTypeItemProps {
  accountType: AccountTypeTreeItem;
  initialIsExcluded: boolean;
  onAccountTypeToggle: (code: number) => void;
}

interface RenderAccountTypesTreeProps {
  accountTypes: AccountTypeTreeItem[];
  initialFormikValues: AccountTypesFormValues;
  onAccountTypeToggle: (code: number) => void;
}

const AccountTypeItem = memo(
  ({
    accountType,
    initialIsExcluded,
    onAccountTypeToggle,
  }: AccountTypeItemProps) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      onAccountTypeToggle(accountType.code);
    };

    return (
      <TreeItem
        key={accountType.id}
        itemId={accountType.id}
        sx={{
          '& .MuiTreeItem-iconContainer': {
            width: '4px',
          },
        }}
        label={
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            gap={1}
            sx={{ width: '100%' }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant='body1'
                sx={{ fontWeight: 500 }}
              >
                {accountType.name}
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
              >
                {accountType.description}
              </Typography>
            </Box>
            <Checkbox
              id={`excludedAccountTypes-${accountType.code}`}
              name='excludedAccountTypes'
              checked={!initialIsExcluded}
              onChange={handleChange}
              onClick={(e) => {
                e.stopPropagation();
              }}
              size='small'
            />
          </Box>
        }
      />
    );
  },
);
AccountTypeItem.displayName = 'AccountTypeItem';

// Render tree - re-renders when Formik values change to update checkbox states
export const RenderAccountTypesTree = memo(
  ({
    accountTypes,
    initialFormikValues,
    onAccountTypeToggle,
  }: RenderAccountTypesTreeProps) => {
    if (!accountTypes.length) return null;

    return (
      <>
        {accountTypes.map((accountType) => {
          const isExcluded = initialFormikValues.excludedAccountTypes.includes(
            accountType.code,
          );
          return (
            <AccountTypeItem
              key={accountType.id}
              accountType={accountType}
              initialIsExcluded={isExcluded}
              onAccountTypeToggle={onAccountTypeToggle}
            />
          );
        })}
      </>
    );
  },
  (prevProps, nextProps) => {
    // Re-render when Formik values change to update checkbox states
    // Compare arrays by reference - if they're different, re-render
    return (
      prevProps.accountTypes === nextProps.accountTypes &&
      prevProps.initialFormikValues.excludedAccountTypes ===
        nextProps.initialFormikValues.excludedAccountTypes &&
      prevProps.onAccountTypeToggle === nextProps.onAccountTypeToggle
    );
  },
);
RenderAccountTypesTree.displayName = 'RenderAccountTypesTree';
